import { cn } from "@repo/ui/lib/utils";
import { Skeleton } from "@repo/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@repo/ui/toggle-group";
import { useQuery } from "@tanstack/react-query";
import { Map as MapIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { type CoverageGrain, fetchDataCoverage } from "@/lib/api";
import {
  buildCoverageGrid,
  type CoverageSource,
  cellValue,
  shadeStep,
} from "@/lib/dataCoverageGrid";

// One hue, light to dark (sequential); step 0 is an empty period.
const SHADE = [
  "bg-muted/40",
  "bg-primary/15",
  "bg-primary/30",
  "bg-primary/50",
  "bg-primary/75",
  "bg-primary",
];
const SOURCES: { value: CoverageSource; label: string }[] = [
  { value: "all", label: "All" },
  { value: "sqm", label: "SQM" },
  { value: "crawl", label: "Crawled" },
];
const GRAINS: { value: CoverageGrain; label: string }[] = [
  { value: "year", label: "Year" },
  { value: "quarter", label: "Quarter" },
  { value: "month", label: "Month" },
];

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString("en-AU");
const monthLabel = (m: string | null) => (m ? m.slice(0, 7) : "none");

/** Which regions hold sales for which periods, by source. Reads data_coverage (refreshed daily). */
export const DataCoverageCard = () => {
  const [state, setState] = useState("VIC");
  const [grain, setGrain] = useState<CoverageGrain>("year");
  const [source, setSource] = useState<CoverageSource>("all");
  const [hover, setHover] = useState<{ region: string; period: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["data-coverage", state, grain],
    queryFn: () => fetchDataCoverage(state, grain),
    staleTime: 10 * 60 * 1000,
  });

  const grid = useMemo(
    () => (data ? buildCoverageGrid(data.cells, grain, source) : null),
    [data, grain, source],
  );
  const summary = data?.states.find((s) => s.state === state);
  const hovered = hover && grid ? grid.byKey.get(`${hover.region}|${hover.period}`) : undefined;
  const labelEvery = grain === "month" ? 12 : grain === "quarter" ? 4 : 1;

  return (
    <div className="card-elevated p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <MapIcon className="w-5 h-5 text-primary" />
            Data coverage
          </h2>
          <p className="text-sm text-muted-foreground">
            Sold sales held per region and period.
            {data?.refreshedAt &&
              ` Refreshed ${new Date(data.refreshedAt).toLocaleString("en-AU", {
                dateStyle: "medium",
                timeStyle: "short",
              })} (daily).`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={grain}
            onValueChange={(v) => v && setGrain(v as CoverageGrain)}
            aria-label="Period"
          >
            {GRAINS.map((g) => (
              <ToggleGroupItem key={g.value} value={g.value}>
                {g.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={source}
            onValueChange={(v) => v && setSource(v as CoverageSource)}
            aria-label="Source"
          >
            {SOURCES.map((s) => (
              <ToggleGroupItem key={s.value} value={s.value}>
                {s.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      {/* One button per state: totals and the latest month each source reaches. */}
      <div className="flex flex-wrap gap-2">
        {(data?.states ?? []).map((s) => (
          <button
            type="button"
            key={s.state}
            onClick={() => setState(s.state)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-left text-xs transition-colors",
              s.state === state ? "border-primary bg-primary/10" : "hover:bg-muted",
            )}
          >
            <span className="font-semibold">{s.state}</span>{" "}
            <span className="text-muted-foreground">{fmt(s.sales)} sales</span>
            <span className="block text-muted-foreground">
              crawled to {monthLabel(s.last_crawl_month)}
            </span>
          </button>
        ))}
      </div>

      {error && (
        <p className="text-sm text-destructive">
          Could not load coverage: {(error as Error).message}
        </p>
      )}
      {isLoading && <Skeleton className="h-64 w-full" />}

      {grid && summary && (
        <>
          <p className="text-sm text-muted-foreground min-h-5">
            {hover ? (
              <>
                <span className="font-medium text-foreground">{hover.region}</span> · {hover.period}
                : {fmt(cellValue(hovered, "all"))} sales (SQM {fmt(hovered?.sqm)}, crawled{" "}
                {fmt(hovered?.crawl)})
              </>
            ) : (
              <>
                {state}: {fmt(summary.sales)} sales in {summary.regions} regions,{" "}
                {monthLabel(summary.first_month)} to {monthLabel(summary.last_month)}. SQM to{" "}
                {monthLabel(summary.last_sqm_month)}, crawled to{" "}
                {monthLabel(summary.last_crawl_month)}. Hover a cell for counts.
              </>
            )}
          </p>

          {grid.regions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sales for this source.</p>
          ) : (
            <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
              <table className="border-separate border-spacing-[2px] text-xs">
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-20 bg-card" />
                    {grid.periods.map((p, i) => (
                      <th
                        key={p}
                        scope="col"
                        className="sticky top-0 z-10 bg-card font-normal text-muted-foreground whitespace-nowrap px-0"
                      >
                        {i % labelEvery === 0 ? p.slice(0, 4) : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody onMouseLeave={() => setHover(null)}>
                  {grid.regions.map(({ region, total }) => (
                    <tr key={region}>
                      <th
                        scope="row"
                        className="sticky left-0 z-10 bg-card pr-2 text-left font-normal whitespace-nowrap max-w-56 truncate"
                        title={`${region}: ${fmt(total)} sales`}
                      >
                        {region}
                      </th>
                      {grid.periods.map((p) => {
                        const v = cellValue(grid.byKey.get(`${region}|${p}`), source);
                        return (
                          <td
                            key={p}
                            aria-label={`${region} ${p}: ${v} sales`}
                            onMouseEnter={() => setHover({ region, period: p })}
                            className={cn(
                              "h-3.5 rounded-[2px]",
                              grain === "month" ? "min-w-1.5" : "min-w-3.5",
                              SHADE[shadeStep(v, grid.max)],
                              hover?.region === region &&
                                hover.period === p &&
                                "ring-1 ring-foreground",
                            )}
                          />
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Fewer</span>
            {SHADE.slice(1).map((c) => (
              <span key={c} className={cn("h-3 w-5 rounded-[2px]", c)} />
            ))}
            <span>More sales (log scale, max {fmt(grid.max)} per cell)</span>
            <span className={cn("ml-3 h-3 w-5 rounded-[2px]", SHADE[0])} />
            <span>none</span>
          </div>
        </>
      )}
    </div>
  );
};
